import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import axios from 'axios';
import { apiString as API_URL } from '../utils/constants';
import Population from '../Models/Population/Population.model';
import OnboardingReportRow from '../Models/AdminOnboarding/OnboardingReportRow.model';
import b64toBlob from 'b64-to-blob';
import CallItem from '../Models/OnboardingWorkflow/CallItem.model';
import { OnboarderStatus } from '../Models/OfficeOnboarding/OnboarderStatus.model';
import { OpenPopulationDto } from '../Models/AdminOnboarding/OpenPopulationDto.model';
import { copyObject } from '../utils/common';
import { setOnboardingProviders } from './onboardingWorkflow.slice';

const adminOnboardingSlice = createSlice({
    name: 'adminOnboardingSlice',
    initialState: {
        assignmentFrequency: 0,
        callItem: new CallItem(),
        callItemDialogOpen: false,
        fetchingReports: false,
        isExporting: false,
        isGeneratingCallList: false,
        isTableFetching: false,
        maxAssignments: 0,
        onboarderStatuses: [] as OnboarderStatus[],
        openPops: [],
        openZipCodes: [],
        openSelectedGroup: false,
        popError: false,
        populations: [],
        populationIds: [],
        reportRows: [],
        selectedGroup: new OpenPopulationDto(),
        selectedOnboarder: new OnboarderStatus(),
        selectedPopulation: new Population(),
        showConfirmDelete: false,
        showEditPopulation: false,
        showInactivePopulations: false,
        undeterminedZipCodes: [],
        zipCodeGroups: [],
        zipError: false,
    },
    reducers: {
        _setAssignmentFrequency(state, action) {
            state.assignmentFrequency = action.payload;
        },
        _setCallItem(state, action) {
            state.callItem = action.payload;
        },
        _setCallItemDialogOpen(state, action) {
            state.callItemDialogOpen = action.payload;
        },
        _setOnboarderStatuses(state, action) {
            state.onboarderStatuses = action.payload;
        },
        _setPopError(state, action) {
            state.popError = action.payload;
        },
        _setFetchingReports(state, action) {
            state.fetchingReports = action.payload;
        },
        _setIsExporting(state, action) {
            state.isExporting = action.payload;
        },
        _setIsGeneratingCallList(state, action) {
            state.isGeneratingCallList = action.payload;
        },
        _setIsTableFetching(state, action) {
            state.isTableFetching = action.payload;
        },
        _setMaxAssignments(state, action) {
            state.maxAssignments = action.payload;
        },
        _setOpenPops(state, action) {
            state.openPops = action.payload;
        },
        _setOpenZipCodes(state, action) {
            state.openZipCodes = action.payload;
        },
        _setOpenSelectionGroup(state, action) {
            state.openSelectedGroup = action.payload;
        },
        _setPopulations(state, action) {
            state.populations = action.payload;
        },
        _setPopulationIds(state, action) {
            state.populationIds = action.payload;
        },
        _setReportRows(state, action) {
            state.reportRows = action.payload;
        },
        _setSelectedGroup(state, action) {
            state.selectedGroup = action.payload;
        },
        _setSelectedOnboarder: (state, action) => {
            state.selectedOnboarder = action.payload;
        },
        _setSelectedPopulation(state, action) {
            state.selectedPopulation = action.payload;
        },
        _setShowConfirmDelete(state, action) {
            state.showConfirmDelete = action.payload;
        },
        _setShowEditPopulation(state, action) {
            state.showEditPopulation = action.payload;
        },
        _setShowInactivePopulations(state, action) {
            state.showInactivePopulations = action.payload;
        },
        _setUndeterminedZipCodes(state, action) {
            state.undeterminedZipCodes = action.payload;
        },
        _setZipCodeGroups(state, action) {
            state.zipCodeGroups = action.payload;
        },
        _setZipError(state, action) {
            state.zipError = action.payload;
        },
    },
});

const {
    _setAssignmentFrequency,
    _setCallItem,
    _setCallItemDialogOpen,
    _setOnboarderStatuses,
    _setPopError,
    _setFetchingReports,
    _setIsExporting,
    _setIsGeneratingCallList,
    _setIsTableFetching,
    _setMaxAssignments,
    _setOpenPops,
    _setOpenSelectionGroup,
    _setOpenZipCodes,
    _setPopulations,
    _setPopulationIds,
    _setReportRows,
    _setSelectedGroup,
    _setSelectedOnboarder,
    _setSelectedPopulation,
    _setShowConfirmDelete,
    _setShowEditPopulation,
    _setShowInactivePopulations,
    _setUndeterminedZipCodes,
    _setZipCodeGroups,
    _setZipError,
} = adminOnboardingSlice.actions;

export const setAssignmentFrequency =
    (value: number): AppThunk =>
    async (dispatch) => {
        dispatch(_setAssignmentFrequency(value));
    };

export const setCallItemDialogOpen =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setCallItemDialogOpen(value));
    };

export const setOnboarderStatuses =
    (value: OnboarderStatus[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setOnboarderStatuses(value));
    };

export const setPopError =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setPopError(value));
    };

export const setFetchingReports =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setFetchingReports(value));
    };

export const setIsExporting =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsExporting(value));
    };

export const setIsTableFetching =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsTableFetching(value));
    };

export const setMaxAssignments =
    (value: number): AppThunk =>
    async (dispatch) => {
        dispatch(_setMaxAssignments(value));
    };

export const setOpenPops =
    (value: OpenPopulationDto[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setOpenPops(value));
    };

export const setOpenSelectedGroup =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setOpenSelectionGroup(value));
    };

export const setOpenZipCodes =
    (value: OpenPopulationDto[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setOpenZipCodes(value));
    };

export const setPopulations =
    (value: Population[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setPopulations(value));
    };

export const setPopulationIds =
    (populationIds: string[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setPopulationIds(populationIds));
    };

export const setReportRows =
    (value: OnboardingReportRow[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setReportRows(value));
    };

export const setSelectedGroup =
    (value: OpenPopulationDto): AppThunk =>
    async (dispatch) => {
        dispatch(_setSelectedGroup(value));
    };

export const setSelectedOnboarder =
    (selectedOnboarder: OnboarderStatus): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_setSelectedOnboarder(selectedOnboarder));
    };

export const setSelectedPopulation =
    (value: Population): AppThunk =>
    async (dispatch) => {
        dispatch(_setSelectedPopulation(value));
    };

export const setShowConfirmDelete =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setShowConfirmDelete(value));
    };

export const setShowEditPopulation =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setShowEditPopulation(value));
    };

export const setShowInactivePopulations =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setShowInactivePopulations(value));
    };

export const setUndeterminedZipCodes =
    (value: string[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setUndeterminedZipCodes(value));
    };

export const setZipCodeGroups =
    (value: OpenPopulationDto[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setZipCodeGroups(value));
    };

export const setZipError =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setZipError(value));
    };

export const fetchPopulations = (): AppThunk => async (dispatch, getState) => {
    dispatch(setFetchingReports(true));
    const response = await axios.get(`${API_URL}/population/GetPopulations`);
    dispatch(setPopulations(response.data));
    dispatch(setFetchingReports(false));
};

export const fetchZipCodeGroups = (): AppThunk => async (dispatch, getState) => {
    const response = await axios.get(`${API_URL}/onboarding/getonboardingzipcodegroups`);
    dispatch(setZipCodeGroups(response.data));
};

export const fetchUndeterminedOnboardingZipCodes = (): AppThunk => async (dispatch, getState) => {
    const response = await axios.get(`${API_URL}/onboarding/getundeterminedonboardingzipcodes`);
    // console.log('fetchUndeterminedOnboardingZipCodes response: ', response.data);
    dispatch(setUndeterminedZipCodes(response.data));
};

export const insertOrUpdateZipCodeGroup =
    (group: OpenPopulationDto): AppThunk =>
    async (dispatch, getState) => {
        await axios.post(`${API_URL}/onboarding/insertorupdatezipcodegroup`, group);
        dispatch(fetchZipCodeGroups());
    };

export const deleteZipCodeGroup =
    (groupId: string): AppThunk =>
    async (dispatch, getState) => {
        await axios.delete(`${API_URL}/onboarding/deletezipcodegroup?groupId=${groupId}`);
        dispatch(setShowConfirmDelete(false));
        dispatch(fetchZipCodeGroups());
    };

export const updatePopulations =
    (populations: Population[]): AppThunk =>
    async (dispatch, getState) => {
        try {
            await axios.post(`${API_URL}/population/updatepopulations`, populations);
        } catch (error) {
            console.log(error);
        }
    };

export const fetchOnboardingReportTable =
    (populationIds: string[]): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsTableFetching(true));
        const response = await axios.post(`${API_URL}/onboarding/GetOnboardingReportTable`, populationIds);
        dispatch(setReportRows(response.data));
        dispatch(setIsTableFetching(false));
    };

export const fetchOnboardingExcelReport =
    (populationIds: string[]): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsExporting(true));
        const response = await axios.post(`${API_URL}/onboarding/GetOnboardingReport`, populationIds);
        if (response.status === 200) {
            const data = await response.data;
            const blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const today = new Date();
            const randomNumber = Math.floor(Math.random() * (1000 - 1 + 1)) + 1;
            const fileName =
                (today.getMonth() + 1 < 10 ? '0' + (today.getMonth() + 1) : today.getMonth() + 1) +
                '.' +
                today.getDate() +
                '.' +
                today.getFullYear() +
                '-' +
                randomNumber;
            document.body.appendChild(a);
            a.href = blobUrl;
            a.download = fileName + '.xlsx';
            a.click();
            window.URL.revokeObjectURL(blobUrl);
            dispatch(setIsExporting(false));
        }
    };

export const getCallerHistoryCallItem =
    (callItemId: string): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/onboarding/GetCallerHistoryCallItem?callItemId=${callItemId}`);
        dispatch(_setCallItem(response.data));
        dispatch(setCallItemDialogOpen(true));
    };

export const clearOnboarderCallList =
    (assignedOnboarderId: string): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/onboarding/ClearOnboarderCallList?assignedOnboarderId=${assignedOnboarderId}`);
        dispatch(setSelectedOnboarder(response.data));
        const onboardingProviders = getState().onboardingWorkflowSlice.onboardingProviders;
        const updatedOnboardingProviders = onboardingProviders.map((onboarderStatus) =>
            onboarderStatus.Id === response.data.Id ? response.data : onboarderStatus
        );
        const sortedList = updatedOnboardingProviders.sort((a, b) => a.Username.localeCompare(b.Username));
        dispatch(setOnboardingProviders(sortedList));
    };

export const skipOnboarderCallList =
    (assignedOnboarderId: string): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/onboarding/SkipOnboarderCallList?assignedOnboarderId=${assignedOnboarderId}`);
        dispatch(setSelectedOnboarder(response.data));
    };

export const generateOnboarderCallList =
    (assignedOnboarderId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_setIsGeneratingCallList(true));
        try {
            const onboarderStatuses = getState().adminOnboardingSlice.onboarderStatuses;
            let copy = copyObject(onboarderStatuses);
            const response = await axios.post(`${API_URL}/onboarding/GetOnboarderStatus?userId=${assignedOnboarderId}`);
            const onboarderStatus = response.data;
            const index = copy.findIndex((os) => os.UserId === assignedOnboarderId);
            if (index !== -1) {
                copy[index] = onboarderStatus;
                dispatch(setOnboarderStatuses(copy));
            }
            dispatch(setSelectedOnboarder(onboarderStatus));
        } finally {
            dispatch(_setIsGeneratingCallList(false));
        }
    };

export const fetchCallListLimits =
    (onboarderStatusId: string): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/onboarding/GetCallListLimits/${onboarderStatusId}`);
        dispatch(setOpenPops(response.data));
    };

export const fetchCallListZipCodeLimits =
    (onboarderStatusId: string): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/onboarding/GetCallListZipCodeLimits/${onboarderStatusId}`);
        dispatch(setOpenZipCodes(response.data));
    };

export default adminOnboardingSlice.reducer;
